<template>
  <SideMenuCollapsed
    class="absolute left-0 lg:flex lg:static top-0 z-40"
    :class="{
      'hidden! lg:flex!': !isMobileSidebarVisible,
      flex: isMobileSidebarVisible,
    }"
    :menu="menu">
    <template #parent-menu-item="{item}">
      <ParentMenuItemCollapsed
        :active-route-name="route.name"
        :aria-label="getLabel(item)"
        :item="item">
        <template #external-menu-item="{child}">
          <ExternalMenuItem
            child
            hide-icon
            :item="child">
            {{ getLabel(child) }}
          </ExternalMenuItem>
        </template>

        <template #internal-menu-item="{child}">
          <InternalMenuItem
            hide-icon
            :is-active="child.to.name === route.name || child.activeRouteNameAliases?.includes(route.name)"
            :item="child"
            :link-component="RouterLink">
            {{ getLabel(child) }}
          </InternalMenuItem>
        </template>
      </ParentMenuItemCollapsed>
    </template>

    <template #external-menu-item="{item}">
      <ExternalMenuItem
        hide-label
        :item="item">
        {{ getLabel(item) }}
      </ExternalMenuItem>
    </template>

    <template #internal-menu-item="{item}">
      <InternalMenuItem
        hide-label
        :is-active="item.to.name === route.name || item.activeRouteNameAliases?.includes(route.name)"
        :item="item"
        :link-component="RouterLink">
        {{ getLabel(item) }}
      </InternalMenuItem>
    </template>

    <template #divider-menu-item>
      <DividerMenuItem />
    </template>

    <ul>
      <DividerMenuItem />
      <ParentMenuItemCollapsed
        :active-route-name="route.name"
        icon-class="text-black-light!"
        :item="moreMenuItem">
        <template #internal-menu-item="{child}">
          <InternalMenuItem
            hide-icon
            :is-active="child.to.name === route.name"
            :item="child"
            :link-component="RouterLink">
            {{ getLabel(child) }}
          </InternalMenuItem>
        </template>

        <li>
          <button
            id="logout-button"
            class="hover:bg-gray-200 px-4 py-2 text-left text-red-600 w-full whitespace-nowrap"
            type="button"
            @click="() => logout()">
            {{ t('account.index.logout') }}
          </button>
        </li>
      </ParentMenuItemCollapsed>
      <DividerMenuItem />
      <ParentMenuItemCollapsed
        :active-route-name="route.name"
        hide-icon
        :item="languageMenuItem">
        <template #label>
          <FlagIcon :country="t('meta.flag')" />
        </template>
        <LanguageToggle
          is-sidebar-collapsed
          :languages="languages"
          @change-language="changeLanguage" />
      </ParentMenuItemCollapsed>
      <DividerMenuItem />
      <li class="hidden lg:block">
        <CollapseMenuButton
          :aria-label="t('ui.navigation.expand')"
          :classes="['pt-3', 'pb-4']"
          :is-collapsed="isSidebarCollapsed"
          @click="() => expandSidebar()" />
      </li>
    </ul>
  </SideMenuCollapsed>
</template>

<script setup lang="ts">
import type {InternalMenuItemConfig} from '@/composables/useMenu/useMenu';
import {useProposition} from '@myparcel-frontend/proposition';
import {
  DividerMenuItem,
  ExternalMenuItem,
  InternalMenuItem,
  ParentMenuItemCollapsed,
  SideMenuCollapsed,
  CollapseMenuButton,
  More24,
  FlagIcon,
  LanguageToggle,
} from '@myparcel-frontend/ui';
import {language} from '@myparcel-frontend/utils';
import {watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {RouterLink, useRoute} from 'vue-router';
import useChangeLanguage from '../../../../composables/useChangeLanguage/useChangeLanguage';
import useAuth from '@/composables/useAuth';
import useMenu from '@/composables/useMenu';
import useShop from '@/composables/useShop/useShop';
import useSidebarToggle from '@/composables/useSidebarToggle';

const {currentShop} = useShop();
const {isSidebarCollapsed, expandSidebar, isMobileSidebarVisible} = useSidebarToggle();
const {getMenu, menu} = useMenu();
const {logout} = useAuth();
const {t} = useI18n();
const {changeLanguage} = useChangeLanguage();
const {proposition} = useProposition();
const route = useRoute();

watch(
  () => currentShop.value?.id,
  () => getMenu(),
  {
    immediate: true,
  },
);

const getLabel = (item: InternalMenuItemConfig) => {
  return t(item.label, item?.multiplyLabel ? 2 : 1);
};

const languages = language.formatLanguageOptions(
  proposition.value.applications.backoffice.internationalization.supportedLanguages,
  proposition.value.applications.backoffice.internationalization.language,
);

const moreMenuItem = {
  type: 'parent',
  icon: More24,
  label: 'more',
  children: [
    {
      to: {name: 'account-settings-contact'},
      label: 'settings.account.title',
      multiplyLabel: true,
      type: 'internal',
    },
  ],
};

const languageMenuItem = {
  type: 'parent',
  children: [],
  label: 'language-toggle',
};
</script>
